import React, {createContext, useContext, useEffect, useState} from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  OAuthProvider,
  signInWithPopup,
  linkWithCredential,
  signInWithCredential,
  signInWithCustomToken,
} from 'firebase/auth';
import {auth, getPopupAuth, fetchSignInMethodsForEmail} from '../firebase';
import {doc, getDoc, setDoc} from 'firebase/firestore';
import {db} from '../firebase';
import {PasswordModal} from '../components/PasswordModal';
import {analytics} from '../services/analytics';
import {clearNoteTemplateCache} from '../services/noteTemplateService';
import {clearPatientChartCache} from '../services/patientChartCache';
import type {
  User as FirebaseUser,
  UserCredential,
  AuthCredential,
} from 'firebase/auth';
import {getReadableAuthErrorMessage} from '../utils/authErrorMessages';
import {linkMicrosoftProvider} from '../services/onboardingService';

const MICROSOFT_OIDC_PROVIDER_ID =
  process.env.REACT_APP_SSO_AZURE_PROVIDER || 'oidc.opalite-entra';

export interface AuthContextValue {
  currentUser: FirebaseUser | null;
  signup: (email: string, password: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  loginWithMicrosoft: () => Promise<UserCredential | void>;
  linkMicrosoftToCurrentAccount: () => Promise<UserCredential | void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  // The app always renders inside AuthProvider, so the undefined default
  // is unreachable in practice.
  return useContext(AuthContext) as AuthContextValue;
}

export function AuthProvider({children}: {children: React.ReactNode}) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingCredential, setPendingCredential] =
    useState<AuthCredential | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [passwordModalError, setPasswordModalError] = useState('');

  function signup(email: string, password: string) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  async function login(email: string, password: string) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    analytics.identify(result.user.uid, {email, login_method: 'email'});
    analytics.capture('user signed in', {login_method: 'email'});
    rememberLastLoginMethod('email');
    return result;
  }

  // Persist the method the user last successfully signed in with so the UI can
  // surface a "Last used" hint next to that option on the auth screen.
  function rememberLastLoginMethod(method: string) {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      window.localStorage.setItem(
        'calliope:lastLoginMethod',
        JSON.stringify({method, at: Date.now()}),
      );
    } catch (e) {
      // Storage may be unavailable (private mode, blocked cookies); ignore.
    }
  }

  // Extract email from Microsoft user account
  function extractEmailFromMicrosoftUser(user: FirebaseUser, authResult: any) {
    // Try multiple sources for email (Microsoft is inconsistent)
    let email = user.email || '';

    // Try OAuth profile data
    if (!email && authResult.additionalUserInfo?.profile) {
      const profile = authResult.additionalUserInfo.profile;
      email =
        profile.email ||
        profile.preferred_username ||
        profile.upn ||
        profile.mail ||
        '';
    }

    return email;
  }

  // Get user document from Firestore
  async function getUserDocument(uid: string) {
    const doc_ref = doc(db, 'User', uid);
    return await getDoc(doc_ref);
  }

  // Create new user document in Firestore
  async function createUserDocument(
    uid: string,
    userData: Record<string, any>,
  ) {
    const doc_ref = doc(db, 'User', uid);
    await setDoc(doc_ref, userData);
  }

  async function attemptServerSideMicrosoftMerge(
    credential: AuthCredential | null,
  ): Promise<UserCredential | null> {
    const idToken = (
      credential as
        | (AuthCredential & {
            idToken?: string;
          })
        | null
    )?.idToken;
    if (!idToken) return null;

    try {
      const {customToken} = await linkMicrosoftProvider(idToken);
      return await signInWithCustomToken(auth, customToken);
    } catch (error) {
      analytics.capture('microsoft_auto_merge_failed', {
        stage: 'server_side_merge',
        error_code: error?.code || 'unknown',
        error_message: (error?.message || '').slice(0, 160),
      });
      return null;
    }
  }

  // Handle account conflict when user exists with different credential
  async function handleAccountConflict(
    error: any,
    microsoftCredential: AuthCredential | null,
  ) {
    const email = error.customData?.email;
    if (!email) {
      throw new Error('Unable to determine conflicting email address');
    }

    // Get existing sign-in methods for this email
    const signInMethods = await fetchSignInMethodsForEmail(auth, email);

    return {
      type: 'ACCOUNT_CONFLICT',
      email,
      existingMethods: signInMethods,
      microsoftCredential,
    };
  }

  async function handleNewUser(user: FirebaseUser, authResult: any) {
    const email = extractEmailFromMicrosoftUser(user, authResult);

    console.log('New Microsoft user detected:', user.uid, email);
    return {
      type: 'NEW_USER',
      user,
      userData: {email},
    };
  }

  // Handle existing user
  async function handleExistingUser(
    user: FirebaseUser,
    userData: Record<string, any>,
  ) {
    // Update linked providers if not already present
    const linkedProviders = userData.linked_providers || [];
    if (!linkedProviders.includes('microsoft')) {
      const updatedData = {
        ...userData,
        linked_providers: [...linkedProviders, 'microsoft'],
        updated_at: new Date(),
      };
      await createUserDocument(user.uid, updatedData);

      return {
        type: 'EXISTING_USER',
        user,
        userData: updatedData,
      };
    }

    return {
      type: 'EXISTING_USER',
      user,
      userData,
    };
  }

  // Core sign-in logic with conflict detection
  async function attemptSignInWithConflictHandling(credential: AuthCredential) {
    try {
      // Try to sign in with Microsoft credential
      const authResult = await signInWithCredential(auth, credential);

      // Check if user document exists in Firestore
      const userDoc = await getUserDocument(authResult.user.uid);

      if (userDoc.exists()) {
        return await handleExistingUser(authResult.user, userDoc.data());
      } else {
        return await handleNewUser(authResult.user, authResult);
      }
    } catch (error) {
      // Handle account exists with different credential
      if (error.code === 'auth/account-exists-with-different-credential') {
        // The original credential's idToken/nonce has been consumed by this
        // failing signInWithCredential call, so reusing it for the subsequent
        // linkWithCredential would fail with auth/invalid-credential. Firebase
        // returns a server-issued "pending" credential on the error itself —
        // that's the one that's safe to keep around for the link step.
        const pendingCredential =
          OAuthProvider.credentialFromError(error) || credential;
        const mergedResult =
          await attemptServerSideMicrosoftMerge(pendingCredential);
        if (mergedResult) {
          const userDoc = await getUserDocument(mergedResult.user.uid);
          return userDoc.exists()
            ? await handleExistingUser(mergedResult.user, userDoc.data())
            : await handleNewUser(mergedResult.user, mergedResult);
        }
        return await handleAccountConflict(error, pendingCredential);
      }
      throw error;
    }
  }

  // Handle sign-in result
  async function handleSignInResult(result: any) {
    switch (result.type) {
      case 'EXISTING_USER':
      case 'NEW_USER':
        console.log(`Microsoft sign-in successful: ${result.type}`);
        // User is automatically set by onAuthStateChanged
        analytics.identify(result.user.uid, {
          email: result.userData?.email,
          login_method: 'microsoft',
        });
        analytics.capture('user signed in', {
          login_method: 'microsoft',
          is_new_user: result.type === 'NEW_USER',
        });
        rememberLastLoginMethod('microsoft');
        break;

      case 'ACCOUNT_CONFLICT':
        console.log('Account conflict detected, showing password modal');
        setPendingCredential(result.microsoftCredential);
        setPendingEmail(result.email);
        setShowPasswordModal(true);
        break;

      default:
        throw new Error('Unknown sign-in result type');
    }
  }

  function buildMicrosoftProvider() {
    const provider = new OAuthProvider(MICROSOFT_OIDC_PROVIDER_ID);
    // Omit `prompt` so Microsoft can silently return a token when the user
    // already has a valid Microsoft session, instead of forcing them to
    // re-enter credentials every time they click "Sign in with Microsoft".
    provider.setCustomParameters({tenant: 'common'});
    provider.addScope('openid');
    provider.addScope('profile');
    provider.addScope('email');
    provider.addScope('User.Read');
    return provider;
  }

  // Main Microsoft sign-in function
  async function loginWithMicrosoft() {
    try {
      const provider = buildMicrosoftProvider();

      // Use the popup-specific auth instance so the popup opens against the
      // default firebaseapp.com handler instead of the custom authDomain.
      const popupAuth = getPopupAuth();
      const authResult = await signInWithPopup(popupAuth, provider);
      const credential = OAuthProvider.credentialFromResult(authResult);

      if (!credential) {
        throw new Error('Unable to get Microsoft credential');
      }

      // Sign in on the main auth instance using the credential from the popup
      const result = await attemptSignInWithConflictHandling(credential);
      await handleSignInResult(result);

      return authResult;
    } catch (error) {
      console.error('Microsoft sign-in error:', error);

      if (error.code === 'auth/account-exists-with-different-credential') {
        const credential = OAuthProvider.credentialFromError(error);
        const mergedResult = await attemptServerSideMicrosoftMerge(credential);
        if (mergedResult) {
          const userDoc = await getUserDocument(mergedResult.user.uid);
          const result = userDoc.exists()
            ? await handleExistingUser(mergedResult.user, userDoc.data())
            : await handleNewUser(mergedResult.user, mergedResult);
          await handleSignInResult(result);
          return mergedResult;
        }
        const result = await handleAccountConflict(error, credential);
        await handleSignInResult(result);
        return;
      }

      if (
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        return;
      }

      // Anything that reaches here is a real Microsoft SSO failure (popup
      // blocked, third-party storage blocked, internal error, network, etc.).
      // Capture it so the next occurrence isn't invisible to telemetry.
      analytics.capture('microsoft_sso_error', {
        stage: 'signInWithPopup',
        error_code: error.code || 'unknown',
        error_name: error.name,
        error_message: (error.message || '').slice(0, 200),
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });

      throw error;
    }
  }

  const handlePasswordSubmit = async (password: string) => {
    if (!pendingCredential) {
      setPasswordModalError(
        'Microsoft credential expired. Please close this dialog and try signing in with Microsoft again.',
      );
      analytics.capture('microsoft_account_linking_error', {
        stage: 'no_pending_credential',
      });
      return;
    }

    // Step 1: authenticate with email/password. This is independent of the
    // Microsoft credential and only fails if the password is actually wrong.
    let result;
    try {
      result = await signInWithEmailAndPassword(auth, pendingEmail!, password);
    } catch (error) {
      console.error('Microsoft link — password sign-in failed:', error);
      analytics.capture('microsoft_account_linking_error', {
        stage: 'password_signin',
        error_code: error.code,
        error_message: (error.message || '').slice(0, 160),
      });
      setPasswordModalError(getReadableAuthErrorMessage(error));
      return;
    }

    // Step 2: link the Microsoft credential we saved earlier. If this fails
    // with auth/invalid-credential it's almost always because the saved OIDC
    // credential is stale (the idToken/nonce was already consumed in the
    // earlier failing signInWithCredential / signInWithPopup). The web SDK's
    // OAuthProvider.credentialFromError gives us a server-issued pending
    // credential that's safe to reuse, but if that's also rejected we surface
    // a clearer message instead of the misleading "incorrect password" one.
    try {
      const linkedResult = await linkWithCredential(
        result.user,
        pendingCredential,
      );

      // Update user document to note the linked provider
      const userDoc = await getUserDocument(result.user.uid);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const linkedProviders = userData.linked_providers || [];
        if (!linkedProviders.includes('microsoft')) {
          const updatedData = {
            ...userData,
            linked_providers: [...linkedProviders, 'microsoft'],
            updated_at: new Date(),
          };
          await createUserDocument(result.user.uid, updatedData);
        }
      }

      setShowPasswordModal(false);
      setPasswordModalError('');
      setPendingCredential(null);
      setPendingEmail(null);

      analytics.capture('microsoft_account_linked', {
        user_id: result.user.uid,
        linked_provider: 'microsoft',
        link_method: 'password_prompt',
      });

      console.log('Successfully linked Microsoft account to existing user');
      return linkedResult;
    } catch (error) {
      console.error('Microsoft link — linkWithCredential failed:', error);
      analytics.capture('microsoft_account_linking_error', {
        stage: 'link_with_credential',
        error_code: error.code,
        error_message: (error.message || '').slice(0, 160),
      });

      // The password actually worked — be explicit so the user doesn't think
      // they typed it wrong and try harder.
      let message;
      if (error.code === 'auth/credential-already-in-use') {
        message =
          'This Microsoft account is already linked to a different account. Please sign in with the original method or contact support.';
      } else if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/missing-or-invalid-nonce'
      ) {
        message =
          'Your password worked, but the Microsoft sign-in expired before we could finish. Please close this dialog and click "Sign in with Microsoft" again.';
      } else {
        message = getReadableAuthErrorMessage(error);
      }
      setPasswordModalError(message);
    }
  };

  function logout() {
    analytics.capture('user signed out');
    analytics.reset();
    // Drop any cached template state so the next user that signs in on
    // this tab doesn't see the previous user's EHR/custom templates.
    clearNoteTemplateCache();
    // Same for the chart panel's in-memory patient data (PHI) — the next
    // user on this tab must never see the previous user's chart.
    clearPatientChartCache();
    return signOut(auth);
  }

  function resetPassword(email: string) {
    return sendPasswordResetEmail(auth, email);
  }

  // Manual account linking for existing users
  async function linkMicrosoftToCurrentAccount() {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Must be signed in to link Microsoft account');
    }

    try {
      const provider = buildMicrosoftProvider();

      // Use the popup-specific auth instance for the OAuth flow
      const popupAuth = getPopupAuth();
      const authResult = await signInWithPopup(popupAuth, provider);
      const microsoftCredential =
        OAuthProvider.credentialFromResult(authResult);

      if (!microsoftCredential) {
        throw new Error('Unable to get Microsoft credential');
      }

      // Link to current account on the main auth instance
      await linkWithCredential(currentUser, microsoftCredential);

      // Update user document to note the linked provider
      const userDoc = await getUserDocument(currentUser.uid);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const linkedProviders = userData.linked_providers || [];
        if (!linkedProviders.includes('microsoft')) {
          const updatedData = {
            ...userData,
            linked_providers: [...linkedProviders, 'microsoft'],
            updated_at: new Date(),
          };
          await createUserDocument(currentUser.uid, updatedData);
        }
      }

      console.log('Successfully linked Microsoft account');
      return authResult;
    } catch (error) {
      console.error('Error linking Microsoft account:', error);

      if (error.code === 'auth/credential-already-in-use') {
        analytics.capture('microsoft_sso_error', {
          stage: 'linkMicrosoftToCurrentAccount',
          error_code: error.code,
          error_message: (error.message || '').slice(0, 200),
        });
        throw new Error(
          'This Microsoft account is already linked to another user',
        );
      }
      if (
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        // Don't throw for user-cancelled popups
        return;
      }

      analytics.capture('microsoft_sso_error', {
        stage: 'linkMicrosoftToCurrentAccount',
        error_code: error.code || 'unknown',
        error_name: error.name,
        error_message: (error.message || '').slice(0, 200),
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });

      throw error;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    loginWithMicrosoft,
    linkMicrosoftToCurrentAccount,
    logout,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPasswordModalError('');
        }}
        onSubmit={handlePasswordSubmit}
        email={pendingEmail}
        error={passwordModalError}
      />
    </AuthContext.Provider>
  );
}
